import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useEditCatalogMutation } from "@modules/catalogs/services/mutations";
import { CatalogFormValues, catalogSchema } from "@modules/catalogs/validations";
import { listCatalogsQueryKey } from "@modules/catalogs/services/queries/listCatalogs/query";

export function useEditCatalogModal(modalRef: RefObject<ModalRef>, onComplete?: () => void) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<CatalogFormValues>({
    resolver: zodResolver(catalogSchema()),
    mode: "onBlur",
  });

  const { mutateAsync, isPending } = useEditCatalogMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listCatalogsQueryKey,
        refetchType: 'all',
      });

      open?.({
        type: "success",
        message: translate("catalog.success.edit"),
      });

      modalRef.current?.close();
      onComplete?.();
    },
  });

  const handleSubmit = async (data: CatalogFormValues) => {
    const catalogId = modalRef.current?.getId();
    await mutateAsync({ id: catalogId as string, data });
  }

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
